import { rawColors, darkColors } from "@/shared/design-system";

// Habillage des TUILES Google Maps selon le thème.
//
// Les marqueurs et le tracé suivent déjà le thème via `themeColor()`, mais le
// FOND de carte est dessiné par Google : sans style, une carte blanche
// éclatante reste plantée au milieu d'une interface sombre — l'écart le plus
// visible de toute la bascule, et le plus éblouissant de nuit.
//
// On ne cherche pas à repeindre la carte aux couleurs de la marque : une carte
// doit rester lisible AVANT d'être jolie. On assombrit les surfaces, on garde
// les routes distinctes du fond, et on laisse les points d'intérêt tranquilles.

/** Style Google Maps — tableau de règles `MapTypeStyle`. */
export type MapStyle = google.maps.MapTypeStyle[];

const DARK_MAP_STYLE: MapStyle = [
  // Base : tout le fond et tous les libellés, repris ensuite au cas par cas.
  { elementType: "geometry", stylers: [{ color: darkColors.white }] },
  { elementType: "labels.text.fill", stylers: [{ color: darkColors.gray500 }] },
  // Contour sombre derrière chaque libellé : sans lui, le texte devient
  // illisible dès qu'il passe sur une route claire.
  { elementType: "labels.text.stroke", stylers: [{ color: darkColors.white }] },
  // Icônes de POI masquées : leurs pastilles de couleur vive sont conçues pour
  // un fond clair et piquent les yeux sur du sombre.
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#16261d" }] },

  // Routes : trois niveaux de gris, du plus discret au plus marqué, pour que la
  // hiérarchie du réseau reste lisible sans éclairer la carte.
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#232b3a" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1a2130" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#2c3648" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3a4557" }] },

  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#202838" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0b1a2a" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d5a7a" }] },
];

/**
 * Style à appliquer pour un thème donné.
 *
 * En clair on rend `undefined` — le style par défaut de Google, que des années
 * d'usage ont rendu familier. On ne le remplace que là où il gêne.
 */
export function mapStyleForTheme(theme: "light" | "dark"): MapStyle | undefined {
  return theme === "dark" ? DARK_MAP_STYLE : undefined;
}

/** Repère de non-régression : la palette claire reste bien la référence. */
export const LIGHT_MAP_BACKGROUND = rawColors.white;
